<template>
  <v-card id="tickets-card" class="max-shadow">
    <v-toolbar class="white">
      <v-toolbar-title class="py-2">
        <div class="title-2">Tickets</div>
      </v-toolbar-title>
      <v-spacer />
      <v-progress-circular
        v-if="loading"
        :size="36"
        :width="3"
        indeterminate
        color="primary"
      />
      <v-btn
        v-else-if="role === 'max-admin'"
        color="primary"
        @click.stop="advanceExport"
      >
        Export
      </v-btn>
      <v-menu offset-y>
        <template v-slot:activator="{ on }">
          <v-btn depressed v-on="on">
            {{ category.title }}
            <v-icon class="pl-2"> arrow_drop_down </v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-tile
            v-for="id in Object.keys(categoryList)"
            :key="id"
            @click="updateCategory(id, categoryList[id])"
          >
            <v-list-tile-title>{{ categoryList[id].title }}</v-list-tile-title>
          </v-list-tile>
        </v-list>
      </v-menu>
      <v-menu offset-y>
        <template v-slot:activator="{ on }">
          <v-btn depressed v-on="on">
            {{ filterBy }}
            <v-icon class="pl-2"> arrow_drop_down </v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-tile
            v-for="(date, index) in dateRange"
            :key="index"
            @click="updateDate(date)"
          >
            <v-list-tile-title>{{ date }}</v-list-tile-title>
          </v-list-tile>
        </v-list>
      </v-menu>
    </v-toolbar>
    <div>
      <v-layout row class="pt-2 pb-1" xl12>
        <v-flex xs12 sm6>
          <count-widget
            color="pink darken-1"
            icon="mdi-ticket-outline"
            title="Open Tickets"
            :value="openTickets"
            style="max-width: 300px; margin: 0 auto"
          />
        </v-flex>
        <v-flex xs12 sm6>
          <count-widget
            color="orange darken-2"
            icon="mdi-ticket-outline"
            title="Closed Tickets"
            :value="closedTickets"
            style="max-width: 300px; margin: 0 auto"
          />
        </v-flex>
        <v-flex xs12 sm6>
          <count-widget
            color="orange darken-2"
            icon="mdi-ticket-outline"
            title="Re-opened Tickets"
            :value="reopenedTickets"
            style="max-width: 300px; margin: 0 auto"
          />
        </v-flex>
        <v-flex xs12 sm6>
          <count-widget
            color="pink darken-1"
            icon="mdi-ticket-outline"
            title="Total Tickets"
            :value="totalTickets"
            style="max-width: 300px; margin: 0 auto"
          />
        </v-flex>
      </v-layout>

      <v-card px-1>
        <v-card-title primary-title class="display-1">
          User Tickets Log
        </v-card-title>
        <div class="px-3">
          <v-progress-linear
            :active="userLoading"
            :indeterminate="true"
            height="2"
            top
          />
        </div>
        <div>
          <v-card-text>
            <barChart
              :chart-data="usersLog"
              :options="chartOptions"
              :height="chartHeight"
            />
          </v-card-text>
        </div>

        <v-card-title primary-title class="display-1">
          Catergory Tickets Log
        </v-card-title>
        <div class="px-3">
          <v-progress-linear
            :active="categoryLoading"
            :indeterminate="true"
            height="2"
            top
          />
        </div>
        <div>
          <v-card-text>
            <barChart
              :chart-data="categoryLog"
              :options="chartOptions"
              :height="chartHeight"
            />
          </v-card-text>
        </div>
      </v-card>
    </div>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="0" bottom>
      {{ snackbarMessage }}
      <v-btn flat @click.native="snackbar = false"> Close </v-btn>
    </v-snackbar>
  </v-card>
</template>

<script>
import * as moment from 'moment';
import { firestore, functions } from '@/firebase';
import TicketCountwidget from '@/components/common/TicketCountWidget';
import _get from 'lodash/get';
import barChart from '@/components/common/BarChart';
import { downloadFile } from '@/utils';
import Papa from 'papaparse';

export default {
  name: 'HelpDeskAnalytics',
  components: {
    'count-widget': TicketCountwidget,
    barChart,
  },
  data() {
    return {
      loading: true,
      userLoading: true,
      categoryLoading: true,
      startAt: '',
      endAt: '',
      snackbar: false,
      snackbarColor: 'primary',
      snackbarMessage: '',
      dateRange: [
        'Today',
        'Yesterday',
        'This week',
        'This month',
        'Last 30 days',
        'Last 60 days',
        'Last 90 days',
        'This Year',
        'Last calendar year',
      ],
      log: {},
      openTickets: 0,
      closedTickets: 0,
      reopenedTickets: 0,
      filterBy: 'Today',
      category: { title: 'Select Category', id: 'all' },
      categoryList: {},
      usersLog: [],
      categoryLog: [],
      chartHeight: 150,
      chartOptions: {
        responsive: true,
        legend: false,
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
              },
              gridLines: {
                display: false,
              },
            },
          ],
          xAxes: [
            {
              gridLines: {
                display: false,
              },
            },
          ],
        },
      },
      yStepSize: 0.25,
      firebaseListeners: [],
      tenants: {},
    };
  },
  computed: {
    role() {
      return _get(this.$store.state.profile, 'role');
    },
    cid() {
      return _get(this.$store.state.profile, 'cid');
    },
    totalTickets() {
      return this.openTickets + this.closedTickets + this.reopenedTickets;
    },
  },
  created() {
    this.tenantsListener();
  },
  beforeDestroy() {
    // unsubscribe all firebase listeners
    this.firebaseListeners.forEach((listenerFunc) => {
      listenerFunc();
    });
  },
  mounted() {
    this.startAt = moment().startOf('day').valueOf();
    this.endAt = moment().endOf('day').valueOf();
    this.fetchCategoryList();
    this.fetchLog();
  },
  methods: {
    tenantsListener() {
      const tenantsListener = firestore
        .collection('tenants')
        .onSnapshot((tenantsSnapshot) => {
          this.tenants = {};
          tenantsSnapshot.forEach((tenantSnapshot) => {
            const tenant = tenantSnapshot.data();
            tenant.id = tenantSnapshot.id;
            this.tenants[tenant.id] = tenant.companyName;
          });
        });
      this.firebaseListeners.push(tenantsListener);
    },
    fetchCategoryList() {
      if (this.role == 'admin') {
        firestore
          .doc(`helpDesk/${this.cid}`)
          .get()
          .then((snapshot) => {
            const docData = (snapshot.data() || {}).categories || {};

            this.categoryList = docData;
            this.categoryList.all = { title: 'All', id: 'all' };
          });
      } else if (this.role == 'max-admin') {
        firestore
          .doc(`helpDesk/buildingSupport`)
          .get()
          .then((snapshot) => {
            const docData = (snapshot.data() || {}).categories || {};

            this.categoryList = docData;
            this.categoryList.all = { title: 'All', id: 'all' };
          });
      }
    },
    fetchTotalTickets() {
      const totalTickets = functions.httpsCallable(
        'bigQuery-helpDesk-totalTickets'
      );
      totalTickets({
        startAt: this.startAt,
        endAt: this.endAt,
        categoryId: this.category.id,
      })
        .then((result) => {
          const data = result.data || {};
          this.openTickets = data.open || 0;
          this.closedTickets = data.closed || 0;
          this.reopenedTickets = data.reopened || 0;

          this.loading = false;
        })
        .catch((err) => {
          console.error(err);
        });
    },
    fetchUserAnalytics() {
      const totalTickets = functions.httpsCallable(
        'bigQuery-helpDesk-userTickets'
      );
      totalTickets({
        startAt: this.startAt,
        endAt: this.endAt,
        categoryId: this.category.id,
      })
        .then((result) => {
          const data = result.data || {};

          let users = [];

          for (const log of data) {
            users.push([log.name, log.count]);
          }

          while (users.length < 10) {
            users.push(['', 0]);
          }

          users.sort((a, b) => {
            return b[1] - a[1];
          });

          users = users.slice(0, 20);

          // option object
          this.usersLog = {
            datasets: [
              {
                label: 'Tickets',
                backgroundColor: '#3366cc',
                data: users.map((log) => log[1]),
              },
            ],
            labels: users.map((log) => log[0]),
          };

          this.userLoading = false;
        })
        .catch((err) => {
          console.error(err);
        });
    },
    fetchCategoryAnalytics() {
      this.categoryLoading = true;
      const totalTickets = functions.httpsCallable(
        'bigQuery-helpDesk-categoryTickets'
      );
      totalTickets({
        startAt: this.startAt,
        endAt: this.endAt,
      })
        .then((result) => {
          const data = result.data || {};

          const categories = [];
          const tickets = [];

          for (const log of data) {
            categories.push(log.name);
            tickets.push(log.count);
          }

          while (categories.length < 10) {
            categories.push('');
            tickets.push(0);
          }

          // option object
          this.categoryLog = {
            datasets: [
              {
                label: 'Tickets',
                backgroundColor: '#3366cc',
                data: tickets,
              },
            ],
            labels: categories,
          };

          this.categoryLoading = false;
        })
        .catch((err) => {
          console.error(err);
        });
    },
    updateCategory(id, value) {
      if (this.category.id === id && this.category.title === value.title)
        return;
      value.id = id;

      this.category = value;
      this.fetchLog();
    },
    fetchLog() {
      this.loading = true;
      this.userLoading = true;
      this.fetchTotalTickets();
      this.fetchUserAnalytics();
      this.fetchCategoryAnalytics();
    },
    updateDate(date) {
      if (this.filterBy === date) return;

      this.filterBy = date;

      switch (this.filterBy) {
        case 'Today':
          this.startAt = moment().startOf('day').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'Yesterday':
          this.startAt = moment().subtract(1, 'days').startOf('day').valueOf();
          this.endAt = moment().subtract(1, 'days').endOf('day').valueOf();

          break;

        case 'This week':
          this.startAt = moment().startOf('week').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'This month':
          this.startAt = moment().startOf('month').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'Last 30 days':
          this.startAt = moment().subtract(29, 'days').startOf('day').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'Last 60 days':
          this.startAt = moment().subtract(59, 'days').startOf('day').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'Last 90 days':
          this.startAt = moment().subtract(89, 'days').startOf('day').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'This Year':
          this.startAt = moment().startOf('year').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'Last calendar year':
          this.startAt = moment().startOf('year').subtract(1, 'year').valueOf();
          this.endAt = moment().endOf('year').subtract(1, 'year').valueOf();
          break;

        default:
          break;
      }

      this.fetchLog();
    },
    advanceExport() {
      this.loading = true;
      const totalTickets = functions.httpsCallable(
        'bigQuery-helpDesk-exportTicketsData'
      );
      totalTickets({
        startAt: this.startAt,
        endAt: this.endAt,
      })
        .then((result) => {
          this.loading = false;
          const data = result.data || {};

          if (!Object.keys(data).length) {
            this.showSnackbar('No data available for export', true);
            return;
          }

          const headers = [
            'title',
            'description',
            'Category Name',
            'Level',
            'Status',
            'Created At',
            // 'Updated At',
            'Company Name',
          ];

          const dataArr = Object.values(data).map((log) => {
            const row = [];
            row.push(log.title || '');
            row.push(log.description || '');
            row.push(log.categoryName || 'Others');
            row.push(log.private || false ? 'tenant' : 'building');
            row.push(log.status);
            row.push(moment(log.createdAt).format('DD-MM-YYYY h:mm A'));
            // row.push(
            //   moment(log.updatedAt).format('DD-MM-YYYY h:mm A')
            // );
            row.push(this.tenants[log.cid]);

            return row;
          });

          const config = {
            quotes: true,
            quoteChar: '"',
            delimiter: ',',
            header: true,
            newline: '\r\n',
          };
          const csvData = {
            fields: headers,
            data: dataArr,
          };
          const csv = Papa.unparse(csvData, config);

          const blob = new Blob([csv], {
            type: 'text/csv;charset=utf-8;',
          });

          const filename = `helpdesk_export_${moment().format(
            'DD_MM_YYYY'
          )}.csv`;
          downloadFile(blob, filename);
          this.loading = false;
        })
        .catch((err) => {
          console.error(err);
          this.loading = false;
        });
    },
    showSnackbar(message, error) {
      // const snackbar = {
      //   message: `${message}`,
      //   color: error ? 'error' : 'success',
      //   show: true,
      // };
      this.snackbar = true;
      this.snackbarMessage = `${message}`;
      this.snackbarColor = error ? 'error' : 'success';
    },
  },
};
</script>

<style>
#tickets-card {
  height: calc(100vh - 158px);
}
</style>
